const RECOVERABLE_FIREBASE_CODES = new Set([
  "aborted",
  "cancelled",
  "deadline-exceeded",
  "network-request-failed",
  "auth/network-request-failed",
  "resource-exhausted",
  "unavailable",
  "unknown",
]);

export function firebaseRecoveryError(error: unknown) {
  const value = error as { code?: unknown; status?: unknown } | null;
  const code = typeof value?.code === "string" ? value.code : "unknown";
  const status = typeof value?.status === "number" ? value.status : undefined;
  return {
    code,
    status,
    retryable:
      RECOVERABLE_FIREBASE_CODES.has(code) ||
      status === 408 ||
      status === 429 ||
      (typeof status === "number" && status >= 500),
  };
}

export type RealtimeLoadError = ReturnType<typeof firebaseRecoveryError> & {
  message: string;
};

export function toRealtimeLoadError(
  error: unknown,
  message: string
): RealtimeLoadError {
  return { ...firebaseRecoveryError(error), message };
}

export function isMissingQueryIndexError(error: unknown) {
  return firebaseRecoveryError(error).code === "failed-precondition";
}

export function isConversationVisibleFromSnapshot(
  item: { hiddenFor?: readonly string[] },
  userUid: string,
  optimisticallyHidden: boolean
) {
  return !optimisticallyHidden && !(item.hiddenFor || []).includes(userUid);
}

export function mergeByStableId<T extends { id: string }>(
  current: readonly T[],
  incoming: readonly T[]
) {
  const merged = new Map(current.map((item) => [item.id, item]));
  incoming.forEach((item) => {
    merged.set(item.id, item);
  });
  return Array.from(merged.values());
}

export function selectRealtimeItems<T>(
  cached: readonly T[],
  fresh: readonly T[],
  hasSuccessfulSnapshot: boolean
) {
  if (fresh.length > 0 || hasSuccessfulSnapshot || cached.length === 0) {
    return [...fresh];
  }
  return [...cached];
}

export function isCacheOnlySnapshot(
  fromCache: boolean,
  hasReceivedServerSnapshot: boolean
) {
  return fromCache && !hasReceivedServerSnapshot;
}

export function isCacheOnlyMissingDocument(
  exists: boolean,
  fromCache: boolean
) {
  return !exists && fromCache;
}

export class ListenerGeneration {
  private value = 0;
  private cleanups = new Set<() => void>();

  get current() {
    return this.value;
  }

  restart() {
    this.disposeCleanups();
    this.value += 1;
    return this.value;
  }

  isCurrent(generation: number) {
    return generation === this.value;
  }

  register(generation: number, cleanup: () => void) {
    if (!this.isCurrent(generation)) {
      cleanup();
      return false;
    }
    this.cleanups.add(cleanup);
    return true;
  }

  dispose(generation: number) {
    if (!this.isCurrent(generation)) return;
    this.disposeCleanups();
    this.value += 1;
  }

  private disposeCleanups() {
    const cleanups = Array.from(this.cleanups);
    this.cleanups.clear();
    cleanups.forEach((cleanup) => cleanup());
  }
}
